import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { Box, Button, TextField, TextareaAutosize, Slider } from "@mui/material";
import { rgb2Hex, rgb2Hls } from "../utils/CommonUtils";
import "./ribbon-effect-component.css";

import Particles, { initParticlesEngine } from "@tsparticles/react";
// import { loadAll } from "@/tsparticles/all"; // if you are going to use `loadAll`, install the "@tsparticles/all" package too.
// import { loadFull } from "tsparticles"; // if you are going to use `loadFull`, install the "tsparticles" package too.
import { loadSlim } from "@tsparticles/slim"; // if you are going to use `loadSlim`, install the "@tsparticles/slim" package too.
import { loadEmittersPlugin } from "@tsparticles/plugin-emitters";
import { AppSingleton } from "../services/user_config";
import { v4 as uuidv4 } from "uuid";

const initialDict = {
  fpsLimit: 30,
  autoPlay: true,
  interactivity: {
    events: {
      onClick: {
        enable: false,
        mode: "emitter",
      },
    },
  },
  modes: {
    emitters: {
      direction: "none",
      spawnColor: {
        value: "#ff0000",
        animation: {
          h: {
            enable: true,
            offset: {
              min: -1.4,
              max: 1.4,
            },
            speed: 0.1,
            sync: false,
          },
          l: {
            enable: true,
            offset: {
              min: 20,
              max: 80,
            },
            speed: 0,
            sync: false,
          },
        },
      },
      life: {
        count: 1,
        duration: 0.1,
        delay: 0.6,
      },
      rate: {
        // delay: 0.1,
        delay: 0.3,
        quantity: 1,
      },
      size: {
        width: 0,
        height: 0,
      },
    },
  },
  particles: {
    number: {
      // value: 20,
      value: 0,
      // density: {
      //   enable: true,
      //   area: 800,
      // },
    },
    // color: {
    //   value: { r: 0, g: 0, b: 0 }, // "#ff0",
    // },
    color: ["#FFC300", "#FF6F61", "#FFD700", "#FF85CF", "#00FF00", "#FF00FF", "#FFC0CB", "#FF69B4", "#FFA500", "#00FFFF"],
    // color: ["#f0b7f4", "#f2b5e1", "#bfdef3", "#b9e9e9", "#ffc9b4"],
    shape: {
      type: ["circle", "square", "polygon"],
      options: {
        polygon: {
          sides: 6,
        },
      },
    },
    opacity: {
      value: { min: 0, max: 1 },
      animation: {
        enable: true,
        speed: 1,
        startValue: "max",
        destroy: "min",
      },
    },
    size: {
      value: { min: 1, max: 3 },
    },
    life: {
      duration: {
        sync: true,
        value: 2,
      },
      count: 1,
    },
    move: {
      enable: true,
      gravity: {
        enable: true,
      },
      drift: {
        min: -2,
        max: 2,
      },
      speed: { min: 1, max: 3 },
      decay: 0.1,
      direction: "none",
      random: true,
      straight: false,
      outModes: {
        default: "destroy",
        top: "none",
      },
    },
    rotate: {
      value: {
        min: 0,
        max: 360,
      },
      direction: "random",
      move: true,
      animation: {
        enable: true,
        speed: 60,
      },
    },
    tilt: {
      direction: "random",
      enable: true,
      move: true,
      value: {
        min: 0,
        max: 360,
      },
      animation: {
        enable: true,
        speed: 60,
      },
    },
    roll: {
      darken: {
        enable: true,
        value: 25,
      },
      enable: true,
      speed: {
        min: 15,
        max: 25,
      },
    },
    wobble: {
      distance: 30, // MARK: default 30
      enable: true,
      move: true,
      speed: {
        min: -15,
        max: 15,
      },
    },
    max_distance: 20, // MARK: no use?
  },
  detectRetina: true,
};

const RIBBON_REVIEW_INTERVAL = 2000;

const RibbonEffectComponent = ({ isReadyRef, scoreRef }) => {
  const [isParticleSystemInit, setIsParticleSystemInit] = useState(false);
  const minSpeedRef = useRef(1);
  const containerRef = useRef(null);
  const emittersRef = useRef([]);
  const engineRef = useRef(null);

  const appSingleton = new AppSingleton();
  const contentColor = appSingleton.isDarkMode ? "white" : "black";

  useEffect(() => {
    initParticlesEngine(async (_engine) => {
      engineRef.current = _engine;
      await loadSlim(_engine);
      await loadEmittersPlugin(_engine);
    }).then(() => {
      setIsParticleSystemInit(true);
    });
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      minSpeedRef.current =
        Object.values(scoreRef.current).reduce((acc, val) => acc + val, 0) / Object.keys(scoreRef.current).length;

      refreshRibbonEffect();
    }, RIBBON_REVIEW_INTERVAL);
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  // useEffect(() => {}, []);

  const refreshRibbonEffect = () => {
    if (isReadyRef.current === false) {
      return;
    }
    const _particlesOptions = particlesOptions;
    _particlesOptions.particles.move = {
      min: Math.floor(minSpeedRef.current * 8),
      max: Math.floor(minSpeedRef.current * 20),
    };

    emittersRef.current.forEach((_uuid) => {
      containerRef.current?.removeEmitter(_uuid);
    });
    emittersRef.current = [];
    [""].forEach(() => {
      const _uuid = uuidv4();
      if (typeof containerRef.current.addEmitter === "function") {
        containerRef.current.addEmitter(
          { ..._particlesOptions, name: _uuid },
          {
            x: 100,
            y: ((appSingleton.windowSize.height * 4) / 3) * 0.95,
          }
        );
      }
      emittersRef.current = [...emittersRef.current, _uuid];
    });
    [""].forEach(() => {
      const _uuid = uuidv4();
      if (typeof containerRef.current.addEmitter === "function") {
        console.log("minSpeedRef");
        console.log(minSpeedRef.current);
        containerRef.current.addEmitter(
          { ..._particlesOptions, name: _uuid },
          {
            x: appSingleton.windowSize.width * 2 - 100,
            y: ((appSingleton.windowSize.height * 4) / 3) * 0.95,
          }
        );
      }
      emittersRef.current = [...emittersRef.current, _uuid];
    });
  };

  const particlesOptions = useMemo(() => {
    const updateDict = initialDict;
    // console.log("minSpeedRef");
    // console.log(minSpeedRef.current);
    // updateDict.particles.move = { min: minSpeedRef.current, max: minSpeedRef.current * 3 };
    return updateDict;
  });

  return (
    <Box>
      {/* <Button
        onClick={() => {
          console.log("click");
          console.log(containerRef.current.plugins.get("emitters"));
          containerRef.current.plugins.get("emitters").array.forEach((el) => {
            console.log(el);
            if (el._paused) {
              el._paused = false;
              el.play();
            } else {
              el._paused = true;
              el.pause();
            }
          });
        }}
      >
        HIIIIIIIII
      </Button> */}
      {/* <Button
        onClick={() => {
          refreshRibbonEffect();
        }}
      >
        Change Color
      </Button> */}
      <Particles
        id="tsparticles"
        particlesLoaded={async (_container) => {
          if (containerRef.current !== _container) {
            containerRef.current = _container;
          }
          // _container.canvas.element.style.zIndex = -1;
          // MARK: add two emitters from start
          // console.log(containerRef.current);
          // console.log(appSingleton.windowSize);
          // typeof _container.addEmitter === "function" &&
          //   _container.addEmitter(particlesOptions, {
          //     x: 100,
          //     y: appSingleton.windowSize.height,
          //   });
          // typeof _container.addEmitter === "function" &&
          //   _container.addEmitter(particlesOptions, {
          //     x: appSingleton.windowSize.width * 2 - 100,
          //     y: appSingleton.windowSize.height,
          //   });
        }}
        options={particlesOptions}
      />
    </Box>
  );
};

const f = () => {};

export { RibbonEffectComponent, f };
